/*
  Сверка протокола, прочитанного со скана (макет 5А, решение владельца
  20.10.2026): слева сам лист, справа то, что система из него прочитала. Читает
  Tesseract у нас - наружу ничего не уходит. Прочитанное только ПРЕДЛАГАЕТСЯ:
  в дом и в хранилище ничего не попало, пока человек не нажал «Сохранить».

  Три вещи система проверить не может или может не до конца, и окно говорит об
  этом прямо, а не молчит:
  - номер: у многих протоколов его нет, и тогда в заявлении будет только дата;
  - организация: если протокол по другой организации, чем в кабинете, окно
    предупреждает, но не запрещает - название в кабинете могли вписать иначе;
  - дом: улицы в протоколе обычно нет, поэтому тот ли это дом, подтверждает сам
    человек флажком, без него «Сохранить» не включается.

  Уже внесённый протокол заменяется только явным нажатием: кнопка называется
  «Заменить», а предупреждение над ней говорит, что именно заменится.

  Компонент ничего не грузит сам: чтение и сохранение приходят пропсами.
*/

import { useEffect, useState } from 'react'

import { date as formatDate } from '../api/format.js'
import { Check, Field, Notice } from './Common.jsx'
import { isEmptyProtocol, protocolBody, protocolPreview } from './protocolValues.js'

const FILE_TYPES = '.pdf,.jpg,.jpeg,.png'

function initial(reading) {
  return {
    protocol_number: reading.protocol_number ?? '',
    protocol_date: reading.protocol_date ?? '',
    management_method: reading.management_method ?? '',
    organisation: reading.organisation ?? '',
  }
}

function useObjectUrl(file) {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return undefined
    const made = URL.createObjectURL(file)
    setUrl(made)
    return () => URL.revokeObjectURL(made)
  }, [file])
  return url
}

function Sheet({ file }) {
  const url = useObjectUrl(file)
  if (!url) return <div className="muted">Скан загружен: {file.name}</div>
  const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
  return (
    <>
      {isPdf ? (
        <iframe className="scan-check__sheet" title="Скан протокола" src={url} />
      ) : (
        <img
          className="scan-check__sheet scan-check__sheet--image"
          alt="Скан протокола"
          src={url}
        />
      )}
      {/* Запасной путь: у части браузеров и телефонов PDF в рамке не рисуется. */}
      <a href={url} target="_blank" rel="noreferrer">
        Открыть скан в новой вкладке
      </a>
    </>
  )
}

function reference(reading) {
  const parts = []
  if (reading.voting_form) parts.push(reading.voting_form)
  if (reading.quorum_percent) parts.push(`кворум ${reading.quorum_percent}%`)
  if (reading.has_quorum === false) parts.push('кворума нет')
  return parts.join(', ')
}

function Warnings({ reading, building }) {
  const facts = reference(reading)
  return (
    <>
      {reading.recognised ? null : (
        <Notice kind="warn">
          Текст на скане прочитать не удалось. Впишите значения по листу слева - файл всё равно
          сохранится.
        </Notice>
      )}
      {reading.existing ? (
        <Notice kind="warn">
          У дома уже внесён протокол
          {reading.existing.protocol_number ? ` № ${reading.existing.protocol_number}` : ''}
          {reading.existing.protocol_date
            ? ` от ${formatDate(reading.existing.protocol_date)}`
            : ''}
          {reading.existing.has_scan ? ' со сканом' : ''}. Сохранение заменит его, и заявления по
          дому пойдут с новыми номером и датой.
        </Notice>
      ) : null}
      {facts ? <p className="muted">Справка (в заявление не идёт): {facts}.</p> : null}
      {reading.house_number ? (
        <p className="muted">
          В протоколе назван дом № {reading.house_number}
          {reading.house_matches === false
            ? `, а в справочнике - «${building.full_address || building.address}». Номера не совпали.`
            : '; улицы в нём обычно нет.'}{' '}
          Тот ли это дом, Арбитр ЖКХ проверить не может - подтвердите сами.
        </p>
      ) : null}
    </>
  )
}

export default function ProtocolScanCheck({
  building,
  file,
  reading,
  readFailure,
  onPick,
  onSave,
  onClose,
}) {
  const [values, setValues] = useState(() => initial(reading))
  const [sameHouse, setSameHouse] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)
  const replacing = Boolean(reading.existing)
  const set = (name) => (event) => setValues({ ...values, [name]: event.target.value })

  async function save(event) {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    try {
      await onSave(
        file,
        {
          ...protocolBody(values),
          management_method: values.management_method.trim(),
          organisation: values.organisation.trim(),
        },
        replacing,
      )
      onClose()
    } catch (error) {
      setFailure(error)
      setBusy(false)
    }
  }

  return (
    <form className="scan-check" onSubmit={save}>
      <div>
        <Sheet file={file} />
        <p className="muted">
          {file.name} · страниц прочитано {reading.pages_read} из {reading.pages_total}
        </p>
      </div>
      <div>
        <p className="muted modal__text">Прочитано со скана. Сверьте с листом слева.</p>
        <div className="row">
          <Field label="Дата протокола" width="s">
            <input
              className="field__input"
              type="date"
              value={values.protocol_date}
              onChange={set('protocol_date')}
            />
          </Field>
          <Field
            label="Номер протокола"
            width="s"
            hint={
              reading.protocol_number
                ? undefined
                : 'Номера в протоколе не нашлось: в заявлении будет только дата. Если номер есть на другом листе, впишите его.'
            }
          >
            <input
              className="field__input"
              value={values.protocol_number}
              maxLength={64}
              onChange={set('protocol_number')}
              placeholder="№"
            />
          </Field>
        </div>
        <div className="scan-check__fields">
          <Field label="Способ управления">
            <input
              className="field__input"
              value={values.management_method}
              onChange={set('management_method')}
            />
          </Field>
          <Field label="Выбранная организация">
            <input
              className="field__input"
              value={values.organisation}
              onChange={set('organisation')}
            />
          </Field>
        </div>
        {reading.organisation_matches === false ? (
          <Notice kind="warn">
            Организация в кабинете - «{reading.cabinet_organisation}». Названия расходятся:
            проверьте, что протокол по вашей организации.
          </Notice>
        ) : null}
        <Warnings reading={reading} building={building} />
        <div className="protocol-preview">
          <span className="protocol-preview__label">В заявлении будет так</span>
          <q>
            …на основании решения общего собрания собственников помещений (
            <mark className="protocol-preview__mark">{protocolPreview(values)}</mark>).
          </q>
        </div>
        <div className="scan-check__confirm">
          <Check
            checked={sameHouse}
            onChange={setSameHouse}
            title="Это протокол по этому дому"
          />
          <span>Это протокол по дому «{building.full_address || building.address}»</span>
        </div>
        <Notice error={failure ?? readFailure} />
        <div className="scan-check__actions">
          <button
            className="button"
            type="submit"
            disabled={busy || !sameHouse || isEmptyProtocol(values)}
          >
            {replacing ? 'Заменить протокол и скан' : 'Сохранить протокол и скан'}
          </button>
          <label className="button button--quiet">
            Заменить файл
            <input
              type="file"
              hidden
              accept={FILE_TYPES}
              aria-label="Другой файл протокола"
              onChange={(event) => onPick(event.target.files?.[0])}
            />
          </label>
          <button className="button button--quiet" type="button" onClick={onClose}>
            Отмена
          </button>
        </div>
      </div>
    </form>
  )
}
